import Storage from '../../../storage';

const STORAGE_PREFIX = 'neogig_tag_styles';

// Style fields the per-feature override may carry. Anything else (property,
// align, ignore/replace/mask functions, etc.) is structural and stays global.
const STYLE_NUMBER_KEYS = ['size', 'offsetX', 'offsetY'];
const STYLE_BOOLEAN_KEYS = ['bold', 'italic', 'box', 'colorize'];
const STYLE_STRING_KEYS = ['color'];

const sanitizeKeyPart = value => {
    const normalized = String(value || 'default')
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');

    return normalized || 'default';
};

const normalizeFeatureId = featureId => {
    if (featureId === null || typeof featureId === 'undefined') {
        return null;
    }

    return String(featureId);
};

const resolveFeatureId = (feature, uniqueField) => {
    if (!feature || !feature.properties) {
        return null;
    }

    const idField = Array.isArray(uniqueField) ? 'Id' : (uniqueField || 'Id');

    return normalizeFeatureId(feature.properties[idField] || feature.properties.Id);
};

const normalizeStyle = style => {
    const out = {};

    if (!style || typeof style !== 'object') {
        return out;
    }

    STYLE_NUMBER_KEYS.forEach(key => {
        if (typeof style[key] !== 'undefined') {
            out[key] = Number(style[key]) || 0;
        }
    });

    STYLE_BOOLEAN_KEYS.forEach(key => {
        if (typeof style[key] !== 'undefined') {
            out[key] = Boolean(style[key]);
        }
    });

    STYLE_STRING_KEYS.forEach(key => {
        if (typeof style[key] !== 'undefined') {
            out[key] = style[key];
        }
    });

    return out;
};

// Stored shape: { [featureId]: { [tagProperty]: { ...styleFields } } }
// Keying by both feature id AND tag property lets a single feature carry
// independent overrides per tag (e.g. its name tag and its VMM value tag).
export const createTagStyleStorageKey = ({ client, layerName, areaType, username }) => [
    STORAGE_PREFIX,
    sanitizeKeyPart(client),
    sanitizeKeyPart(username),
    sanitizeKeyPart(layerName),
    sanitizeKeyPart(areaType)
].join(':');

const persist = (scope, styles) => {
    const key = createTagStyleStorageKey(scope);

    if (Object.keys(styles).length) {
        Storage.save(key, styles);
    } else {
        Storage.remove(key);
    }
};

export const getSavedTagStyles = scope => Storage.get(createTagStyleStorageKey(scope)) || {};

export const saveTagStyle = (scope, featureId, property, style) => {
    const normalizedFeatureId = normalizeFeatureId(featureId);

    if (!normalizedFeatureId || !property) {
        return getSavedTagStyles(scope);
    }

    const styles = getSavedTagStyles(scope);
    styles[normalizedFeatureId] = Object.assign({}, styles[normalizedFeatureId], {
        [property]: normalizeStyle(style)
    });
    persist(scope, styles);

    return styles;
};

export const removeSavedTagStyle = (scope, featureId, property) => {
    const normalizedFeatureId = normalizeFeatureId(featureId);
    const styles = getSavedTagStyles(scope);
    const featureStyles = normalizedFeatureId && styles[normalizedFeatureId];

    if (!property || !featureStyles || !featureStyles[property]) {
        return styles;
    }

    delete featureStyles[property];

    if (!Object.keys(featureStyles).length) {
        delete styles[normalizedFeatureId];
    }

    persist(scope, styles);

    return styles;
};

// Clears every saved override for a given tag property across all features.
// Omitting `property` wipes the whole scope (all tags).
export const clearTagStyles = (scope, property) => {
    if (!property) {
        Storage.remove(createTagStyleStorageKey(scope));
        return {};
    }

    const styles = getSavedTagStyles(scope);

    Object.keys(styles).forEach(featureId => {
        if (styles[featureId] && styles[featureId][property]) {
            delete styles[featureId][property];

            if (!Object.keys(styles[featureId]).length) {
                delete styles[featureId];
            }
        }
    });

    persist(scope, styles);

    return styles;
};

export const getFeatureTagStyle = (feature, styles = {}, uniqueField = 'Id', property) => {
    if (!property) {
        return null;
    }

    const featureId = resolveFeatureId(feature, uniqueField);
    const featureStyles = featureId && styles[featureId];

    return featureStyles && featureStyles[property] ? normalizeStyle(featureStyles[property]) : null;
};

export const applyFeatureTagStyle = (tagOptions, feature, styles = {}, uniqueField = 'Id') => {
    if (!tagOptions || typeof tagOptions !== 'object') {
        return tagOptions;
    }

    const override = getFeatureTagStyle(feature, styles, uniqueField, tagOptions.property);

    if (!override) {
        return tagOptions;
    }

    return {
        ...tagOptions,
        ...override
    };
};



// WEBPACK FOOTER //
// ./src/scripts/layers/Leaflet/VectorLayer/TagStyleStorage.js